import { useState } from "react";
import { cn } from "@/lib/utils";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LineChart, Line, Tooltip } from "recharts";
import { CheckCircle2, Zap, Building2, Sparkles } from "lucide-react";

const STEPS = [
  {
    id: 1, title: "Vos factures deviennent des écritures — sans saisie.",
    kicker: "01 · OCR + Factur-X",
    desc: "Glissez un lot de PDF fournisseurs. L'IA extrait montant, TVA, IBAN, échéance, assigne le compte comptable et détecte les doublons.",
    metric: "12 factures traitées en 8s",
    bullets: [
      "Facturation Factur-X native (obligation 2026)",
      "Extraction montants, TVA, IBAN, échéances",
      "Rapprochement automatique commande ↔ facture",
    ],
    icon: Zap,
  },
  {
    id: 2, title: "Vos banques dialoguent enfin avec votre compta.",
    kicker: "02 · Multi-banques DSP2",
    desc: "Connectez Crédit Agricole, BNP, Société Générale, Qonto, Revolut en 30 secondes. Les mouvements se rapprochent tout seuls, en temps réel.",
    metric: "3 banques agrégées",
    bullets: [
      "Connecteurs DSP2 (Bridge, Powens)",
      "Rapprochement IA multi-priorité",
      "Prévisionnel glissant 90 jours",
    ],
    icon: Building2,
  },
  {
    id: 3, title: "Clôture mensuelle — sans crise de nerfs.",
    kicker: "03 · Compta & FEC",
    desc: "Journaux auto, TVA calculée, écritures d'à-nouveau générées. Export FEC / Cegid / Sage en un clic pour votre expert-comptable.",
    metric: "Clôture J+3",
    bullets: [
      "Écritures automatiques par règles OCR",
      "TVA CA3 / CA12 + Autoliquidation",
      "Export FEC certifié, exports comptables tiers",
    ],
    icon: CheckCircle2,
  },
  {
    id: 4, title: "Le Board Pack CFO — prêt en 30 secondes.",
    kicker: "04 · Pilotage IA",
    desc: "Marges, CA, DSO, DPO, runway, top 10 clients, top 10 fournisseurs. Un rapport PDF prêt à envoyer au board tous les mois.",
    metric: "30s pour le board pack",
    bullets: [
      "Health Score et alertes IA",
      "Multi-entités et consolidation groupe",
      "Export PDF board-ready et Excel",
    ],
    icon: Sparkles,
  },
];

const CHART_1 = [
  { d: "Lun", v: 42 }, { d: "Mar", v: 51 }, { d: "Mer", v: 48 },
  { d: "Jeu", v: 63 }, { d: "Ven", v: 74 }, { d: "Sam", v: 21 }, { d: "Dim", v: 18 },
];
const CHART_2 = [
  { m: "Jan", ca: 42, dep: 28 }, { m: "Fév", ca: 48, dep: 30 },
  { m: "Mar", ca: 55, dep: 32 }, { m: "Avr", ca: 51, dep: 30 },
  { m: "Mai", ca: 63, dep: 33 }, { m: "Jui", ca: 71, dep: 35 },
];

export default function ProductTour() {
  const [active, setActive] = useState(1);
  const step = STEPS.find((s) => s.id === active);

  return (
    <section
      id="tour"
      className="py-20 lg:py-28 bg-white relative"
      data-testid="product-tour"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#4F46E5]">
            Product tour · 4 minutes
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B0F17]">
            De la facture au board pack — sans quitter la plateforme.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Steps nav */}
          <div className="lg:col-span-4 space-y-2">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const on = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  data-testid={`tour-step-${s.id}`}
                  className={cn(
                    "w-full text-left rounded-xl border p-4 transition-all",
                    on
                      ? "bg-[#0B0F17] text-white border-[#0B0F17] shadow-lg shadow-slate-900/10"
                      : "bg-white border-slate-200 hover:border-slate-300",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        "h-8 w-8 rounded-lg grid place-items-center shrink-0",
                        on ? "bg-indigo-500/20 text-indigo-300" : "bg-slate-50 text-slate-500",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div
                        className={cn(
                          "text-[10px] font-semibold uppercase tracking-widest",
                          on ? "text-indigo-300" : "text-slate-400",
                        )}
                      >
                        {s.kicker}
                      </div>
                      <div
                        className={cn(
                          "mt-1 text-sm font-semibold leading-snug",
                          on ? "text-white" : "text-slate-800",
                        )}
                      >
                        {s.title}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Mockup */}
          <div className="lg:col-span-8">
            <div
              key={active}
              className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 shadow-xl shadow-slate-900/[0.06] overflow-hidden animate-in fade-in duration-500"
              data-testid={`tour-panel-${active}`}
            >
              <div className="px-6 py-4 border-b border-slate-100 bg-white flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-500">
                  {step.kicker}
                </div>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                  {step.metric}
                </span>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#0B0F17] leading-tight">
                  {step.title}
                </h3>
                <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
                  {step.desc}
                </p>

                <ul className="mt-5 space-y-2">
                  {step.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>

                {/* Chart mockup */}
                <div className="mt-6 rounded-xl border border-slate-100 bg-white p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                      {active === 3 || active === 4
                        ? "Évolution CA / Dépenses (k€)"
                        : "Activité de la semaine"}
                    </div>
                    <div
                      className="text-xs text-slate-400 font-mono"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      demo · {new Date().toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 208 }}>
                    <ResponsiveContainer width="100%" height={208} minWidth={100}>
                      {active === 3 || active === 4 ? (
                        <LineChart data={CHART_2}>
                          <XAxis
                            dataKey="m"
                            stroke="#94A3B8"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                          <Tooltip
                            contentStyle={{
                              border: "1px solid #E2E8F0",
                              borderRadius: 8,
                              fontSize: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="ca"
                            stroke="#4F46E5"
                            strokeWidth={2.5}
                            dot={{ r: 3 }}
                            name="CA"
                          />
                          <Line
                            type="monotone"
                            dataKey="dep"
                            stroke="#059669"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            strokeDasharray="4 4"
                            name="Dépenses"
                          />
                        </LineChart>
                      ) : (
                        <BarChart data={CHART_1}>
                          <XAxis
                            dataKey="d"
                            stroke="#94A3B8"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                          <Tooltip
                            contentStyle={{
                              border: "1px solid #E2E8F0",
                              borderRadius: 8,
                              fontSize: 12,
                            }}
                          />
                          <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                            {CHART_1.map((_, i) => (
                              <Cell
                                key={i}
                                fill={i === 4 ? "#4F46E5" : "#E0E7FF"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
